import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Layout,
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  message,
  Spin,
  Empty,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  RobotOutlined,
  UserOutlined
} from '@ant-design/icons';
import { chatApi } from '../services/api';

const { Content, Sider } = Layout;
const { TextArea } = Input;
const { Text, Title } = Typography;

const ChatPage = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const messagesEndRef = useRef(null);

  // 滚动到消息底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 加载会话列表
  const loadSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const response = await chatApi.getUserSessions();
      if (response.data.success) {
        setSessions(response.data.data);
        // 如果没有当前会话且有会话列表，选择第一个
        if (!currentSession && response.data.data.length > 0) {
          setCurrentSession(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('加载会话列表失败:', error);
      message.error('加载会话列表失败');
    } finally {
      setSessionsLoading(false);
    }
  }, [currentSession]);

  // 加载会话消息
  const loadMessages = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    try {
      const response = await chatApi.getSessionMessages(sessionId);
      if (response.data.success) {
        setMessages(response.data.data);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('加载消息失败:', error);
      message.error('加载消息失败');
    }
  }, []);

  // 发送消息
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    try {
      const response = await chatApi.sendMessage(messageText, currentSession?.id);
      if (response.data.success) {
        const { userMessage, assistantMessage, sessionId } = response.data.data;
        
        // 如果是新会话，更新当前会话ID
        if (!currentSession || currentSession.id !== sessionId) {
          await loadSessions();
          // 使用callback形式获取最新的sessions状态
          setSessions(prevSessions => {
            const newSession = prevSessions.find(s => s.id === sessionId) || { id: sessionId, title: '新的聊天' };
            setCurrentSession(newSession);
            return prevSessions;
          });
        }
        
        // 更新消息列表
        setMessages(prev => [...prev, userMessage, assistantMessage]);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      message.error('发送消息失败');
    } finally {
      setLoading(false);
    }
  };

  // 创建新会话
  const createNewSession = async () => {
    try {
      const response = await chatApi.createSession();
      if (response.data.success) {
        const newSession = response.data.data;
        setSessions(prev => [newSession, ...prev]);
        setCurrentSession(newSession);
        setMessages([]);
        message.success('新会话创建成功');
      }
    } catch (error) {
      console.error('创建会话失败:', error);
      message.error('创建会话失败');
    }
  };

  // 删除会话
  const deleteSession = async (sessionId) => {
    try {
      const response = await chatApi.deleteSession(sessionId);
      if (response.data.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (currentSession?.id === sessionId) {
          const remainingSessions = sessions.filter(s => s.id !== sessionId);
          setCurrentSession(remainingSessions[0] || null);
          setMessages([]);
        }
        message.success('会话删除成功');
      }
    } catch (error) {
      console.error('删除会话失败:', error);
      message.error('删除会话失败');
    }
  };

  // 更新会话标题
  const updateSessionTitle = async (sessionId, title) => {
    try {
      const response = await chatApi.updateSessionTitle(sessionId, title);
      if (response.data.success) {
        setSessions(prev => prev.map(s => 
          s.id === sessionId ? { ...s, title } : s
        ));
        if (currentSession?.id === sessionId) {
          setCurrentSession(prev => ({ ...prev, title }));
        }
        message.success('标题更新成功');
      }
    } catch (error) {
      console.error('更新标题失败:', error);
      message.error('更新标题失败');
    }
  };

  // 处理键盘事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (currentSession) {
      loadMessages(currentSession.id);
    }
  }, [currentSession, loadMessages]);

  return (
    <Layout style={{ height: 'calc(100vh - 64px)' }}>
      {/* 会话列表侧边栏 */}
      <Sider width={300} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
        <div style={{ padding: '16px' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            onClick={createNewSession}
            style={{ marginBottom: 16 }}
          >
            新建聊天
          </Button>
          
          <Title level={5}>聊天历史</Title>
          
          {sessionsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin />
            </div>
          ) : sessions.length === 0 ? (
            <Empty description="暂无聊天记录" />
          ) : (
            <List
              dataSource={sessions}
              renderItem={(session) => (
                <List.Item
                  key={session.id}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    backgroundColor: currentSession?.id === session.id ? '#e6f7ff' : 'transparent',
                  }}
                  onClick={() => setCurrentSession(session)}
                  actions={[
                    <Tooltip title="编辑标题">
                      <EditOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTitle(session.id);
                          setNewTitle(session.title);
                        }}
                      />
                    </Tooltip>,
                    <Popconfirm
                      title="确定删除这个会话吗？"
                      onConfirm={(e) => {
                        e.stopPropagation();
                        deleteSession(session.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      editingTitle === session.id ? (
                        <Input
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onPressEnter={() => {
                            updateSessionTitle(session.id, newTitle);
                            setEditingTitle(null);
                          }}
                          onBlur={() => setEditingTitle(null)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Text ellipsis style={{ fontSize: '14px' }}>
                          {session.title}
                        </Text>
                      )
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {new Date(session.createdAt).toLocaleDateString()}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Sider>

      {/* 聊天主区域 */}
      <Content style={{ display: 'flex', flexDirection: 'column' }}>
        {currentSession ? (
          <>
            {/* 聊天头部 */}
            <div style={{ 
              padding: '16px', 
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa'
            }}>
              <Title level={4} style={{ margin: 0 }}>
                {currentSession.title}
              </Title>
            </div>

            {/* 消息列表 */}
            <div style={{ 
              flex: 1, 
              padding: '16px', 
              overflowY: 'auto',
              backgroundColor: '#f8f9fa'
            }}>
              {messages.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '50px',
                  color: '#999'
                }}>
                  <RobotOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>开始与AI助手对话吧！</div>
                </div>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {messages.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      style={{
                        display: 'flex',
                        justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Card
                        style={{
                          maxWidth: '70%',
                          backgroundColor: msg.role === 'USER' ? '#1890ff' : '#fff',
                          color: msg.role === 'USER' ? '#fff' : '#000',
                          border: msg.role === 'USER' ? 'none' : '1px solid #f0f0f0',
                        }}
                        bodyStyle={{ padding: '12px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <Avatar
                            size="small"
                            icon={msg.role === 'USER' ? <UserOutlined /> : <RobotOutlined />}
                            style={{
                              backgroundColor: msg.role === 'USER' ? '#fff' : '#1890ff',
                              color: msg.role === 'USER' ? '#1890ff' : '#fff',
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.content}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              opacity: 0.7, 
                              marginTop: '4px'
                            }}>
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </Space>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 输入区域 */}
            <div style={{ 
              padding: '16px', 
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fff'
            }}>
              <Space.Compact style={{ width: '100%' }}>
                <TextArea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="输入您的消息..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendMessage}
                  loading={loading}
                  disabled={!inputMessage.trim()}
                  style={{ height: 'auto' }}
                >
                  发送
                </Button>
              </Space.Compact>
            </div>
          </>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            color: '#999'
          }}>
            <RobotOutlined style={{ fontSize: '64px', marginBottom: '16px' }} />
            <Title level={3} type="secondary">选择一个会话开始聊天</Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={createNewSession}>
              创建新会话
            </Button>
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default ChatPage;